import { create } from "zustand";
import axios from "api/common";
import { apiFetchMe, type AuthMeResponse } from "api/auth/me";
import { setBridgeAccessToken } from "auth/accessTokenBridge";

export type MeStatus = "idle" | "loading" | "ready";

export type AuthState = {
  accessToken: string | null;
  me: AuthMeResponse | null;
  meStatus: MeStatus;
  setAccessToken: (token: string | null) => void;
  setMe: (me: AuthMeResponse | null) => void;
  hasPermission: (permission: string) => boolean;
  fetchMe: () => Promise<void>;
  refreshAccessToken: () => Promise<boolean>;
  reset: () => void;
};

const syncBridge = (token: string | null) => {
  setBridgeAccessToken(token);
};

export const useAuthStore = create<AuthState>()((set, get) => ({
  accessToken: null,
  me: null,
  meStatus: "idle",

  setAccessToken: (token: string | null) => {
    syncBridge(token);
    set({ accessToken: token });
  },

  setMe: (me: AuthMeResponse | null) => set({ me }),

  hasPermission: (permission: string) => {
    const perms = get().me?.permissions;
    if (!perms) {
      return false;
    }
    return perms.includes(permission);
  },

  fetchMe: async () => {
    set({ meStatus: "loading" });
    try {
      const { data } = await apiFetchMe();
      set({ me: data, meStatus: "ready" });
    } catch {
      set({ me: null, meStatus: "ready" });
    }
  },

  refreshAccessToken: async () => {
    try {
      const { data } = await axios.post<{ access: string }>(
        "/auth/token/refresh/",
        {},
        { withCredentials: true }
      );
      if (data?.access) {
        get().setAccessToken(data.access);
        return true;
      }
    } catch {
      /* refresh inválido o sin cookie */
    }
    return false;
  },

  reset: () => {
    syncBridge(null);
    set({ accessToken: null, me: null, meStatus: "idle" });
  },
}));
